import type {Dimensions, TouchingBoundary} from "../Scene.tsx";
import {lightenColor} from "./shapes/ShapeEffects.ts";

type BoundariesProps = {
    scene: Dimensions;
    intensity: number;
    touched: TouchingBoundary;
}

export function Boundaries({scene, intensity, touched}: BoundariesProps) {

    const {width, height, depth} = scene;

    const hsw = width / 2; // half sceneWidth
    const hsh = height / 2; // half sceneHeight
    const hsd = depth / 2; // half sceneDepth

    const pt = 0.2; // plate thickness
    const hpt = pt / 2; // half plate thickness

    // colors
    const backBorder = "#333333"; // back plate
    const highlightBack = "#999999";
    const lrBorders = "#227722"; // left and right borders
    const tbBorders = "#222299"; // top and bottom borders
    const highlightLR = lightenColor(lrBorders, 0.6);
    const highlightTB = lightenColor(tbBorders, 0.8);

    // border material with common properties
    const boundsRenderOrder = -1;
    const borderMaterial = (color: string, emissive: string, isTouched: boolean) => (
        <meshStandardMaterial
            color={color}
            emissive={emissive}
            emissiveIntensity={isTouched ? 0.1 : intensity}
            opacity={0.5}
        />
    );

    const backPlateMaterial = (color: string, emissive: string, isTouched: boolean) => (
        <meshStandardMaterial
            color={color}
            emissive={emissive}
            emissiveIntensity={isTouched ? 0.08 : 0.02}
            opacity={0.01}
        />
    );

    return (
        <>
            {/* Back plate */}
            <mesh position={[0, 0, -depth - hpt]} renderOrder={boundsRenderOrder}>
                <boxGeometry args={[width, height, pt]}/>
                {backPlateMaterial(backBorder, highlightBack, touched.back)}
            </mesh>

            {/* Left plate */}
            <mesh position={[-hsw - hpt, 0, -hsd]} rotation={[0, -Math.PI / 2, 0]} renderOrder={boundsRenderOrder}>
                <boxGeometry args={[depth, height, pt]}/>
                {borderMaterial(lrBorders, highlightLR, touched.left)}
            </mesh>

            {/* Right plate */}
            <mesh position={[hsw + hpt, 0, -hsd]} rotation={[0, -Math.PI / 2, 0]} renderOrder={boundsRenderOrder}>
                <boxGeometry args={[depth, height, pt]}/>
                {borderMaterial(lrBorders, highlightLR, touched.right)}
            </mesh>

            {/* Top plate */}
            <mesh position={[0, hsh + hpt, -hsd]} rotation={[Math.PI / 2, 0, 0]} renderOrder={boundsRenderOrder}>
                <boxGeometry args={[width, depth, pt]}/>
                {borderMaterial(tbBorders, highlightTB, touched.top)}
            </mesh>

            {/* Bottom plate */}
            <mesh position={[0, -hsh - hpt, -hsd]} rotation={[Math.PI / 2, 0, 0]} renderOrder={boundsRenderOrder}>
                <boxGeometry args={[width, depth, pt]}/>
                {borderMaterial(tbBorders,  highlightTB, touched.bottom)}
            </mesh>
        </>
    );
}